import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import { useRef, useState } from 'react';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import postApi from '../../../api/post.api';
import { ContentEditor, WebsiteInput } from '../../../components';
import CategorySelect from '../../../components/CategorySelect';
import ImageUploadWithPreview from '../../../components/ImageUpload';
import { ListIncubatorInput } from '../../../components/ListIncubatorInput';
import TopicSelect from '../../../components/TopicSelect';
import { useQuery } from '../../../hooks/query';
import { EComponent } from '../../../models/base';
import { EPostType, EScope, IPostModel } from '../../../models/post.model';
import { Container } from './style';

export default function GreenHubCreatePage() {
  const query = useQuery<{ component: EComponent }>();
  const [data, setData] = useState<Partial<IPostModel>>({
    type: EPostType.Link,
    scope: EScope.GreenHub,
    component: query.component,
  });
  const navigate = useNavigate();
  const contentEditorRef = useRef<ContentEditor>(null);
  const websiteInputRef = useRef<WebsiteInput>(null);
  const listItemInputEnRef = useRef<ListIncubatorInput>(null);
  const listItemInputViRef = useRef<ListIncubatorInput>(null);

  const [loading, setLoading] = useState(false);

  return (
    <Container>
      <Box
        sx={{
          width: '100%',
          p: 2,
          boxSizing: 'border-box',
        }}
      >
        <Grid container columnSpacing={2} rowSpacing={4}>
          <Grid item xs={12}>
            <Typography variant="h3" p={1}>
              Create new Green Hub card
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h5" p={1}>
              Cover Image
            </Typography>

            <ImageUploadWithPreview
              value={data?.picture}
              onChange={(value) => {
                if (data) {
                  setData({
                    ...data,
                    picture: value,
                  });
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h5" p={1}>
              Background Image
            </Typography>

            <ImageUploadWithPreview
              value={data?.background_image}
              onChange={(value) => {
                if (data) {
                  setData({
                    ...data,
                    background_image: value,
                  });
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" p={1}>
              Order
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <TextField
                label="Order"
                variant="outlined"
                size="small"
                value={data?.order || 0}
                onChange={(ev) => {
                  if (data) {
                    setData({
                      ...data,
                      order: +ev.currentTarget.value || 0,
                    });
                  }
                }}
                type="number"
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" p={1}>
              Name
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Vietnamese"
              variant="outlined"
              size="small"
              value={data?.title_vi || ''}
              onChange={(ev) => {
                if (data) {
                  setData({
                    ...data,
                    title_vi: ev.currentTarget.value,
                  });
                }
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="English"
              variant="outlined"
              size="small"
              value={data?.title_en || ''}
              onChange={(ev) => {
                if (data) {
                  setData({
                    ...data,
                    title_en: ev.currentTarget.value,
                  });
                }
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" p={1}>
              Detail
            </Typography>
          </Grid>
          <Grid item sm={6} lg={3}>
            <CategorySelect
              component={data?.component || EComponent.Connect}
              value={data?.category?.id}
              onChange={(item) => {
                if (data) {
                  setData({
                    ...data,
                    category: item,
                    category_id: item?.id ?? null,
                  });
                }
              }}
            />
          </Grid>
          <Grid item sm={6} lg={3}>
            <TopicSelect
              component={data?.component || EComponent.Connect}
              value={data?.tags?.[0]?.id}
              onChange={(item) => {
                if (data) {
                  setData({
                    ...data,
                    tags: item ? [item] : [],
                    tag_ids: item?.id ? [item.id] : [],
                  });
                }
              }}
            />
          </Grid>
          <Grid item sm={6} lg={3}>
            <TextField
              label="Time to read (min)"
              variant="outlined"
              size="small"
              value={data?.reading_min || 0}
              onChange={(ev) => {
                if (data) {
                  setData({
                    ...data,
                    reading_min: +ev.currentTarget.value || 0,
                  });
                }
              }}
              type="number"
              fullWidth
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="Published at"
              variant="outlined"
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              value={
                data?.published_at
                  ? moment(data.published_at).format('YYYY-MM-DDTHH:mm')
                  : null
              }
              onChange={(ev) => {
                if (data) {
                  setData({
                    ...data,
                    published_at: ev.currentTarget.value,
                  });
                }
              }}
              type="datetime-local"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h5" p={1}>
              Component
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <Select
                value={data?.component || EComponent.Connect}
                onChange={(e) => {
                  if (data) {
                    setData({
                      ...data,
                      component: e.target.value as EComponent,
                      tags: [],
                      tag_ids: [],
                      category: null,
                      category_id: null,
                    });
                  }
                }}
              >
                {Object.values(EComponent).map((t) => (
                  <MenuItem key={t} value={t}>
                    {_.capitalize(_.words(t).join(' '))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h5" p={1}>
              Content type
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <Select
                value={data.type}
                onChange={(e) => {
                  if (data) {
                    setData({
                      ...data,
                      type: e.target.value as EPostType,
                    });
                  }
                }}
              >
                {Object.values(EPostType).map((t) => (
                  <MenuItem key={t} value={t}>
                    {_.capitalize(_.words(t).join(' '))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {data.type === EPostType.Blog && (
            <ContentEditor
              ref={contentEditorRef}
              initData={{
                content_en: Array.isArray(data?.content_en)
                  ? ''
                  : data?.content_en,
                content_vi: Array.isArray(data?.content_vi)
                  ? ''
                  : data?.content_vi,
              }}
            />
          )}
          {data.type === EPostType.Link && (
            <WebsiteInput ref={websiteInputRef} initData={data} />
          )}

          {data.type === EPostType.Table && (
            <>
              <Grid item xs={12}>
                <Typography variant="h5" p={1}>
                  English Data
                </Typography>
              </Grid>
              <ListIncubatorInput
                ref={listItemInputEnRef}
                initData={
                  Array.isArray(data?.content_en) ? data.content_en : []
                }
              />
              <Grid item xs={12}>
                <Typography variant="h5" p={1}>
                  Vietnamese Data
                </Typography>
              </Grid>
              <ListIncubatorInput
                ref={listItemInputViRef}
                initData={
                  Array.isArray(data?.content_vi) ? data.content_vi : []
                }
              />
            </>
          )}
          <Grid item xs={12}>
            <Typography variant="h5" p={1}>
              Description
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Vietnamese"
              variant="outlined"
              multiline
              minRows={5}
              size="small"
              value={data?.short_content_vi || ''}
              onChange={(ev) => {
                if (data) {
                  setData({
                    ...data,
                    short_content_vi: ev.currentTarget.value,
                  });
                }
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="English"
              variant="outlined"
              size="small"
              multiline
              value={data?.short_content_en || ''}
              minRows={5}
              onChange={(ev) => {
                if (data) {
                  setData({
                    ...data,
                    short_content_en: ev.currentTarget.value,
                  });
                }
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              disabled={loading || !data}
              sx={{ minWidth: 200 }}
              variant="contained"
              onClick={() => {
                if (data) {
                  setLoading(true);
                  const getContent = () => {
                    switch (data?.type) {
                      case EPostType.Blog: {
                        return contentEditorRef.current?.getContentData();
                      }
                      case EPostType.Link: {
                        return websiteInputRef.current?.getUrl();
                      }
                      case EPostType.Table: {
                        return {
                          content_en: listItemInputEnRef.current?.getData(),
                          content_vi: listItemInputViRef.current?.getData(),
                        };
                      }

                      default: {
                        return {};
                      }
                    }
                  };

                  postApi
                    .create({
                      ...data,
                      ...getContent(),
                      component: data?.component || EComponent.Connect,
                    })
                    .then((res) => {
                      toast.success('Update successful');
                      navigate(`/green-hub/${res.data.id}`);
                    })
                    .finally(() => {
                      setLoading(false);
                    });
                }
              }}
            >
              Save
            </Button>
          </Grid>
        </Grid>
        <div style={{ height: 100 }} />
      </Box>
    </Container>
  );
}
